interface PopupCardData {
  titulo: string;
  conteudo: string;
  imagem_url: string;
  link_url: string;
  link_texto: string;
}

interface PopupCardProps {
  popup: PopupCardData;
  onClose: () => void;
  closing?: boolean;
}

function isExternal(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Visual do pop-up reutilizável — usado tanto na exibição no site
 * (GlobalPopup) quanto na pré-visualização dentro do painel admin.
 */
export default function PopupCard({ popup, onClose, closing = false }: PopupCardProps) {
  const hasConteudo = !!popup.conteudo && popup.conteudo.trim().length > 0;
  const hasBotao = !!popup.link_url;
  const hasImagem = !!popup.imagem_url;
  const linkProps = popup.link_url && isExternal(popup.link_url)
    ? { target: '_blank' as const, rel: 'noopener noreferrer' }
    : {};

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9998] transition-opacity duration-300 ${closing ? 'opacity-0' : 'opacity-100'}`}
        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
      />

      {/* Popup */}
      <div
        className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6 transition-all duration-300 ${closing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
      >
        <div
          className="relative w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #001a1c 0%, #002a2e 60%, #001a1c 100%)',
            border: '1px solid rgba(0,197,200,0.2)',
          }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-colors"
            style={{ background: 'rgba(0,0,0,0.55)', color: 'rgba(255,255,255,0.8)' }}
            aria-label="Fechar pop-up"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          {/* Scrollable body */}
          <div className="overflow-y-auto">
            {/* Image — always contained so it never overflows */}
            {hasImagem && (
              <div className="w-full flex items-center justify-center bg-black/30">
                {hasBotao ? (
                  <a
                    href={popup.link_url}
                    {...linkProps}
                    onClick={onClose}
                    className="block max-w-full cursor-pointer"
                    title={popup.titulo}
                  >
                    <img
                      src={popup.imagem_url}
                      alt={popup.titulo || 'Popup'}
                      className="block w-auto h-auto max-w-full max-h-[62vh] object-contain mx-auto"
                    />
                  </a>
                ) : (
                  <img
                    src={popup.imagem_url}
                    alt={popup.titulo || 'Popup'}
                    className="block w-auto h-auto max-w-full max-h-[62vh] object-contain mx-auto"
                  />
                )}
              </div>
            )}

            {/* Content */}
            {(hasConteudo || hasBotao) && (
              <div className="p-6 md:p-8">
                {hasConteudo && (
                  <>
                    <div
                      className="text-white/80 text-sm leading-relaxed space-y-3"
                      dangerouslySetInnerHTML={{ __html: popup.conteudo }}
                    />
                    <style>{`
                      .animate-in h1, .animate-in h2, .animate-in h3 { color: #00C5C8; margin-bottom: 0.5rem; }
                      .animate-in strong { color: #00C5C8; }
                      .animate-in p { color: rgba(255,255,255,0.7); line-height: 1.7; }
                    `}</style>
                  </>
                )}

                {hasBotao && (
                  <a
                    href={popup.link_url}
                    {...linkProps}
                    onClick={onClose}
                    className="inline-block mt-5 px-6 py-3 rounded-lg font-semibold text-sm cursor-pointer whitespace-nowrap transition-all duration-300 hover:opacity-90"
                    style={{ background: '#00C5C8', color: '#001416' }}
                  >
                    {popup.link_texto || 'Saiba mais'}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}